import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import CandidateLayout, { StatusPill } from "./CandidateLayout";
import { useProfessions } from "../hooks/useProfessions";
import { useAppT } from "../components/home/i18n";
import { Save, X, Plus, Lock } from "lucide-react";

const AVAILABILITY = ["Immediate", "Within 1 month", "Within 3 months", "Flexible"];
const LEVELS = ["Basic", "Conversational", "B2", "Fluent", "Native"];

const Field = ({ label, children, hint }) => (
  <div>
    <label className="text-xs font-medium text-[#5C5855]">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-[#8a8580]">{hint}</p>}
  </div>
);
const inputCls = "mt-1 w-full rounded-sm border border-[#E8E4DB] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1F4A38] focus:ring-1 focus:ring-[#1F4A38]";

const Section = ({ title, children }) => (
  <div className="rounded-md border border-[#E8E4DB] bg-white p-6">
    <h2 className="font-serif text-xl text-[#173A2C]">{title}</h2>
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

export default function ProfileEditor() {
  const professions = useProfessions();
  const { a } = useAppT();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [countryInput, setCountryInput] = useState("");
  const [langRow, setLangRow] = useState({ lang: "", level: "Fluent" });

  const load = async () => {
    const { data } = await API.get("/candidate/profile");
    setP(data);
  };
  useEffect(() => { load(); }, []);

  if (!p) return <CandidateLayout><div className="text-[#5C5855]">{a.common.loading}</div></CandidateLayout>;

  const locked = ["submitted", "under_review"].includes(p.review_status);
  const setPriv = (k, v) => setP({ ...p, private: { ...p.private, [k]: v } });
  const setPub = (k, v) => setP({ ...p, public: { ...p.public, [k]: v } });

  const toggleProfession = (id) => {
    const has = p.professions.includes(id);
    const professions = has ? p.professions.filter((x) => x !== id) : [...p.professions, id];
    const role_skills = { ...p.role_skills };
    if (has) delete role_skills[id]; else role_skills[id] = role_skills[id] || [];
    setP({ ...p, professions, role_skills });
  };
  const toggleSkill = (pid, skill) => {
    const cur = p.role_skills[pid] || [];
    const next = cur.includes(skill) ? cur.filter((s) => s !== skill) : [...cur, skill];
    setP({ ...p, role_skills: { ...p.role_skills, [pid]: next } });
  };

  const addCountry = () => {
    const c = countryInput.trim();
    if (c && !p.public.preferred_countries.includes(c)) setPub("preferred_countries", [...p.public.preferred_countries, c]);
    setCountryInput("");
  };
  const addLang = () => {
    if (langRow.lang.trim()) setPub("languages", [...p.public.languages, { ...langRow, lang: langRow.lang.trim() }]);
    setLangRow({ lang: "", level: "Fluent" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/candidate/profile", {
        private: p.private, professions: p.professions, role_skills: p.role_skills, public: p.public,
      });
      setP(data);
      toast.success(a.profile.saved);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <CandidateLayout statusBadge={<StatusPill status={p.status_display} />}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-serif text-3xl text-[#173A2C]">{a.profile.title}</h1><p className="mt-1 text-sm text-[#5C5855]">{a.profile.completeness} {p.completeness}% {a.profile.multiProf}</p></div>
        <button data-testid="save-profile-btn" onClick={save} disabled={saving || locked}
          className="inline-flex items-center gap-2 rounded-full bg-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? a.common.saving : a.common.save}</button>
      </div>

      {locked && (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-[#D28E3D]/40 bg-[#D28E3D]/10 p-3 text-sm text-[#8a5a1f]">
          <Lock className="h-4 w-4" /> {a.profile.locked}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={a.profile.secIdentity}>
          <p className="text-[11px] text-[#8a8580]">{a.profile.secIdentityHint}</p>
          <Field label={a.profile.legalName}><input data-testid="pf-legal-name" className={inputCls} value={p.private.legal_name || ""} onChange={(e) => setPriv("legal_name", e.target.value)} disabled={locked} /></Field>
          <Field label={a.profile.phone}><input data-testid="pf-phone" className={inputCls} value={p.private.phone || ""} onChange={(e) => setPriv("phone", e.target.value)} disabled={locked} /></Field>
          <Field label={a.profile.exactAddress}><input className={inputCls} value={p.private.exact_address || ""} onChange={(e) => setPriv("exact_address", e.target.value)} disabled={locked} /></Field>
        </Section>

        <Section title={a.profile.secLocation}>
          <Field label={a.profile.broadLocation} hint={a.profile.broadLocationHint}><input data-testid="pf-location" className={inputCls} value={p.public.broad_location || ""} onChange={(e) => setPub("broad_location", e.target.value)} disabled={locked} /></Field>
          <Field label={a.profile.experience}><input data-testid="pf-experience" type="number" min="0" className={inputCls} value={p.public.experience_years ?? ""} onChange={(e) => setPub("experience_years", e.target.value === "" ? null : parseInt(e.target.value))} disabled={locked} /></Field>
          <Field label={a.profile.availability}>
            <select data-testid="pf-availability" className={inputCls} value={p.public.availability || ""} onChange={(e) => setPub("availability", e.target.value)} disabled={locked}>
              <option value="">{a.profile.select}</option>
              {AVAILABILITY.map((av) => <option key={av} value={av}>{a.profile.availabilityOpts[av] || av}</option>)}
            </select>
          </Field>
        </Section>

        <Section title={a.profile.secProfessions}>
          <p className="text-[11px] text-[#8a8580]">{a.profile.professionsHint}</p>
          <div className="flex flex-wrap gap-2">
            {professions.map((pr) => (
              <button key={pr.id} data-testid={`prof-${pr.id}`} onClick={() => toggleProfession(pr.id)} disabled={locked}
                className={`rounded-full border px-3 py-1.5 text-sm ${p.professions.includes(pr.id) ? "border-[#1F4A38] bg-[#1F4A38] text-white" : "border-[#E8E4DB] text-[#5C5855]"}`}>{pr.label}</button>
            ))}
          </div>
          {p.professions.map((pid) => {
            const pr = professions.find((x) => x.id === pid);
            if (!pr) return null;
            return (
              <div key={pid} className="rounded-sm border border-[#E8E4DB] p-3">
                <div className="text-xs font-semibold text-[#1F4A38]">{pr.label} {a.profile.skillsSuffix}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {pr.skills.map((s) => (
                    <button key={s} data-testid={`skill-${pid}-${s}`} onClick={() => toggleSkill(pid, s)} disabled={locked}
                      className={`rounded-full border px-2.5 py-1 text-xs ${(p.role_skills[pid] || []).includes(s) ? "border-[#C4A46B] bg-[#C4A46B]/15 text-[#8a6f38]" : "border-[#E8E4DB] text-[#5C5855]"}`}>{s}</button>
                  ))}
                </div>
              </div>
            );
          })}
        </Section>

        <Section title={a.profile.secLanguages}>
          <Field label={a.profile.languages}>
            <div className="mt-1 space-y-1.5">
              {p.public.languages.map((l, i) => (
                <div key={i} className="flex items-center gap-2 rounded-sm border border-[#E8E4DB] px-3 py-1.5 text-sm">
                  <span className="flex-1">{l.lang} · <span className="text-[#5C5855]">{a.profile.levels[l.level] || l.level}</span></span>
                  <button onClick={() => setPub("languages", p.public.languages.filter((_, j) => j !== i))} disabled={locked} className="text-[#8B3A3A]"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input data-testid="lang-input" placeholder={a.profile.languagePlaceholder} className={inputCls + " mt-0"} value={langRow.lang} onChange={(e) => setLangRow({ ...langRow, lang: e.target.value })} disabled={locked} />
              <select className={inputCls + " mt-0 w-36"} value={langRow.level} onChange={(e) => setLangRow({ ...langRow, level: e.target.value })} disabled={locked}>{LEVELS.map((l) => <option key={l} value={l}>{a.profile.levels[l] || l}</option>)}</select>
              <button data-testid="add-lang-btn" onClick={addLang} disabled={locked} className="rounded-sm border border-[#1F4A38] px-3 text-[#1F4A38]"><Plus className="h-4 w-4" /></button>
            </div>
          </Field>
          <Field label={a.profile.preferredCountries}>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {p.public.preferred_countries.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#1F4A38]/6 border border-[#1F4A38]/20 px-2.5 py-1 text-xs text-[#1F4A38]">{c}
                  <button onClick={() => setPub("preferred_countries", p.public.preferred_countries.filter((x) => x !== c))} disabled={locked}><X className="h-3 w-3" /></button></span>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input data-testid="country-input" placeholder={a.profile.addCountry} className={inputCls + " mt-0"} value={countryInput} onChange={(e) => setCountryInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCountry())} disabled={locked} />
              <button data-testid="add-country-btn" onClick={addCountry} disabled={locked} className="rounded-sm border border-[#1F4A38] px-3 text-[#1F4A38]"><Plus className="h-4 w-4" /></button>
            </div>
          </Field>
          <Field label={a.profile.training}><input className={inputCls} value={p.public.training || ""} onChange={(e) => setPub("training", e.target.value)} disabled={locked} /></Field>
          <Field label={a.profile.headline}><input data-testid="pf-headline" className={inputCls} value={p.public.headline || ""} onChange={(e) => setPub("headline", e.target.value)} disabled={locked} /></Field>
          <Field label={a.profile.bio}><textarea rows="3" className={inputCls} value={p.public.bio || ""} onChange={(e) => setPub("bio", e.target.value)} disabled={locked} /></Field>
        </Section>
      </div>
    </CandidateLayout>
  );
}
